/**
 * ForgeCockpit — the /forge home, a WhatsApp-style comms cockpit.
 *   Pane 1: Tenders (in-sourcing)            [TenderListPane]
 *   Pane 2: Tender detail (items/docs/quotes) [TenderDetailPane]
 *   Panes 3+4: Chats + Conversation + actions [reuses SourcingPanel's 3-pane cockpit]
 * A slim Validate/Submit bar in the header opens the validation gate + submit/
 * Manifest handoff (ValidationPanel) in a drawer. Spec/bid-requirement editing
 * lives at /forge/:id (reachable via the "open workspace" link in pane 1).
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShieldCheck, X, RefreshCw, Loader2, Pencil, Download } from 'lucide-react';
import { ForgeProvider, useForge } from '../ForgeContext';
import { forgeAPI } from '../../../services/api';
import TenderListPane from './TenderListPane';
import TenderDetailPane from './TenderDetailPane';
import NewProductModal from './NewProductModal';
import DocsStage from './DocsStage';
import AccioChatPane from './AccioChatPane';
import AccioStatusStrip from './AccioStatusStrip';
import OperatorAgentPane from './OperatorAgentPane';
import SourcingPanel from '../panels/SourcingPanel';
import BidRequirementsPanel from '../panels/BidRequirementsPanel';
import SpecSheetPanel from '../panels/SpecSheetPanel';
import QuotesPanel from '../panels/QuotesPanel';
import ValidationPanel from '../panels/ValidationPanel';
import SuppliersPanel from '../panels/SuppliersPanel';
import { statusMeta } from '../sourcingStatus';

// Titles for the facet header.
const FACET_TITLE = { accio: 'Accio', chats: 'Suppliers & chats',
  requirements: 'Bid requirements', docs: 'Tender documents', quotes: 'Quotes' };
const facetTitle = (v) => (v.kind === 'item' ? `Line item #${v.id}` : (FACET_TITLE[v.kind] || ''));

// Reference facets DOCK in the right rail beside the chat (line item = a full-width
// datasheet PREVIEW; bid requirements). Editing a datasheet opens a modal with the edit
// tools. Workspaces (Suppliers & chats, Docs, Quotes, Accio) TAKE OVER full-width.
const DOCK_FACETS = new Set(['item', 'requirements']);
const facetMode = (kind) => (kind === 'agent' ? 'chat' : (DOCK_FACETS.has(kind) ? 'dock' : 'takeover'));

// The facet the navigator selected — opens BESIDE the persistent chat (right panel).
// 'agent' is handled by the center chat, so it never reaches here.
const Stage = ({ view, tenderId }) => {
  switch (view?.kind) {
    case 'item': return <SpecSheetPanel focusLineItem={view.id} />;
    case 'requirements': return <BidRequirementsPanel />;
    case 'docs': return <DocsStage />;
    case 'quotes': return <QuotesPanel />;
    // Per-tender: the pane scopes to this tender; the bridge binds the tender
    // context so the agent works it and routes replies back to this thread.
    case 'accio': return <AccioChatPane tenderId={tenderId} />;
    case 'chats':
    default: return <SourcingPanel hideAccio />;
  }
};

// Header + navigator/stage region + validate drawer — runs inside ForgeProvider.
// Sourcing is triggered by the operator in the Accio chat (the 'accio' stage) —
// no queue, no chip. FORGE just receives Accio's write-back (suppliers/quotes/docs).
const CockpitMain = () => {
  const { tenderId, summary, refresh, preparing } = useForge();
  const [showValidate, setShowValidate] = useState(false);
  // A tender opens straight into the FORGE Operator Agent — the conversational driver.
  const [view, setView] = useState({ kind: 'agent' });
  const mode = facetMode(view.kind);   // 'chat' | 'dock' | 'takeover'
  const [editItem, setEditItem] = useState(false);   // datasheet edit modal
  // Close the edit modal whenever the selected facet changes.
  useEffect(() => { setEditItem(false); }, [view.kind, view.id]);
  const pass = summary?.validation?.overall_pass;
  const gate = pass == null ? null : pass;

  // Tender just sent to FORGE: spec generates in the background. Poll until ready.
  const sheet = summary?.spec_sheet;
  const specPreparing = !!sheet && sheet.forge_status !== 'ready'
    && (sheet.forge_status === 'preparing' || !(sheet.line_items || []).length);
  useEffect(() => {
    if (!specPreparing) return undefined;
    const id = setInterval(() => { refresh().catch(() => {}); }, 6000);
    return () => clearInterval(id);
  }, [specPreparing, refresh]);

  return (
    <div className="forge-ck-main">
      <div className="forge-ck-header">
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', minWidth: 0 }}>
          <span style={{ fontWeight: 700, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {summary?.spec_sheet?.title || summary?.title || tenderId}
          </span>
          {summary?.sourcing_status && (() => { const m = statusMeta(summary.sourcing_status); return (
            <span className="forge-rel" style={{ background: `${m.color}22`, color: m.color, flexShrink: 0 }}>{m.label}</span>
          ); })()}
          {(preparing || specPreparing) && <span className="forge-muted" style={{ fontSize: 12, flexShrink: 0 }}><Loader2 size={12} className="forge-spin" /> preparing spec…</span>}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="forge-btn-ghost" onClick={() => refresh()} title="Refresh"><RefreshCw size={14} /></button>
          <button
            className={`forge-btn-primary ${gate === false ? 'forge-ck-gate-fail' : ''}`}
            onClick={() => setShowValidate(true)}>
            <ShieldCheck size={14} /> Validate &amp; Submit
            {gate != null && <span className="forge-ck-gate-dot" style={{ background: gate ? '#16a34a' : '#d97706' }} />}
          </button>
        </div>
      </div>

      {/* Always-on Accio status — keeps FORGE and the execution agent in sync. */}
      <AccioStatusStrip tenderId={tenderId} />

      <div className={`forge-ck-main-row mode-${mode}`}>
        <TenderDetailPane view={view} onSelect={setView} />
        {/* The FORGE Agent chat is the persistent center for chat + dock modes. */}
        {mode !== 'takeover' && (
          <div className="forge-ck-stage forge-ck-chat">
            <OperatorAgentPane tenderId={tenderId} />
          </div>
        )}
        {/* A reference facet docks in the right rail; a workspace facet takes over full-width. */}
        {(mode === 'dock' || mode === 'takeover') && (
          <aside className={mode === 'dock' ? 'forge-ck-rightpanel' : 'forge-ck-takeover'}>
            <div className="forge-ck-rp-head">
              <span>{facetTitle(view)}</span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {view.kind === 'item' && (
                  <button className="forge-btn-ghost" onClick={() => setEditItem(true)}>
                    <Pencil size={14} /> Edit
                  </button>
                )}
                <button className="forge-btn-ghost" onClick={() => setView({ kind: 'agent' })}
                  title="Back to chat"><X size={15} /></button>
              </div>
            </div>
            <div className="forge-ck-stage" key={`${view.kind}:${view.id ?? ''}`}>
              {view.kind === 'item'
                ? <SpecSheetPanel focusLineItem={view.id} showEditor={false} hideToolbar />
                : <Stage view={view} tenderId={tenderId} />}
            </div>
          </aside>
        )}
      </div>

      {/* Editing a datasheet opens a roomy modal with the edit tools (chat editor). */}
      {editItem && view.kind === 'item' && (
        <div className="forge-ds-modal-backdrop" onClick={() => setEditItem(false)}>
          <div className="forge-ds-modal" onClick={(e) => e.stopPropagation()}>
            <div className="forge-ds-modal-head">
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700 }}>
                <Pencil size={16} /> Edit — {facetTitle(view)}
              </span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <a className="forge-btn-ghost" href={forgeAPI.specSheetExportUrl(tenderId)}
                  target="_blank" rel="noreferrer"><Download size={14} /> Export PDF</a>
                <button className="forge-btn-ghost" onClick={() => setEditItem(false)}><X size={16} /></button>
              </div>
            </div>
            <div className="forge-ds-modal-body">
              <SpecSheetPanel focusLineItem={view.id} showEditor hideToolbar />
            </div>
          </div>
        </div>
      )}

      {showValidate && (
        <div className="forge-ck-drawer-backdrop" onClick={() => setShowValidate(false)}>
          <aside className="forge-ck-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="forge-ck-drawer-head">
              <span style={{ fontWeight: 700 }}>Validate &amp; Submit</span>
              <button className="forge-btn-ghost" onClick={() => setShowValidate(false)}><X size={16} /></button>
            </div>
            <div className="forge-ck-drawer-body"><ValidationPanel /></div>
          </aside>
        </div>
      )}
    </div>
  );
};

const ForgeCockpit = () => {
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('tender') || null;
  const select = (id) => setParams(id ? { tender: id } : {}, { replace: true });
  const [showNew, setShowNew] = useState(false);
  const [showSuppliers, setShowSuppliers] = useState(false);
  const [listReload, setListReload] = useState(0);

  const onSourced = (tenderId) => {
    setShowNew(false);
    setListReload((k) => k + 1);   // refetch the tender list so the new product shows
    if (tenderId) select(tenderId); // and open it straight into the cockpit
  };

  return (
    // .forge-page provides the --forge-* palette tokens; global dark mode (html.dark,
    // toggled from the top nav) themes it via the html.dark .forge-page overrides.
    <div className="forge-page forge-cockpit-page">
      <div className="forge-cockpit-grid">
        <TenderListPane selectedId={selectedId} onSelect={select}
          reloadKey={listReload} onNew={() => setShowNew(true)}
          onSuppliers={() => setShowSuppliers(true)} />
        {selectedId ? (
          <ForgeProvider key={selectedId} tenderId={selectedId}>
            <CockpitMain />
          </ForgeProvider>
        ) : (
          <div className="forge-ck-empty">
            <div>
              <div style={{ fontSize: 28, marginBottom: 8 }}>🛠️</div>
              Select a tender to see its items, documents, supplier chats and quotes.
              <div style={{ marginTop: 14 }}>
                <button className="forge-btn-primary" onClick={() => setShowNew(true)}>
                  ＋ New product to source
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {showNew && <NewProductModal onClose={() => setShowNew(false)} onSourced={onSourced} />}
      {/* Supplier console is cross-tender, so it lives on the outer shell rather
          than inside a tender's ForgeProvider. Same drawer pattern as Validate. */}
      {showSuppliers && (
        <div className="forge-ck-drawer-backdrop" onClick={() => setShowSuppliers(false)}>
          <aside className="forge-ck-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="forge-ck-drawer-head">
              <span style={{ fontWeight: 700 }}>Supplier console</span>
              <button className="forge-btn-ghost" onClick={() => setShowSuppliers(false)}><X size={16} /></button>
            </div>
            <div className="forge-ck-drawer-body"><SuppliersPanel /></div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default ForgeCockpit;
